import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import type React from "react";
import { useEffect } from "react";
import X from "@/assets/icons/x.svg";
import { Loader } from "@/components/common/loader";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useMultidietPopup } from "@/features/multidiet-picker/api/hooks/use-multidiet-popup";
import { MultidietPicker } from "@/features/multidiet-picker/components/multidiet-picker";

export const MultidietPickerPopup = ({
  setOpen,
  open,
  children,
}: {
  setOpen: (open: boolean) => void;
  open: boolean;
  children: React.ReactNode;
}) => {
  const t = useTranslations("common");
  const params = useSearchParams();
  const { data: popupData, isPending } = useMultidietPopup({
    options: {
      retry: 3,
      enabled: open,
    },
  });

  useEffect(() => {
    if (open) {
      setOpen(false);
    }
  }, [params]);

  return (
    <Dialog modal={true} onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild className="">
        {children}
      </DialogTrigger>
      <DialogTitle className="hidden" />
      <DialogContent
        className="flex h-svh max-h-svh min-w-[300px] flex-col gap-0 rounded-none p-0 max-md:w-full max-md:max-w-full md:h-[calc(100svh-100px)] md:max-h-[770px] lg:min-w-[900px] lg:rounded-xl"
        closeButton={false}
      >
        <DialogDescription className="hidden" />
        <div className="h-full overflow-scroll">
          <DialogClose className="absolute top-4 right-4 rounded-full bg-white p-1 md:top-10 lg:top-8 lg:right-10">
            <X className="h-6 w-6" />
          </DialogClose>
          {isPending && <Loader />}
          {popupData?.data && (
            <MultidietPicker data={popupData.data} layout="popup" />
          )}
          {/* The picker is configured in the CMS - without it show an error, not an endless loader. */}
          {!(isPending || popupData?.data) && (
            <p className="px-6 py-16 text-center font-medium text-lg">
              {t("somethingWentWrong")}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
